// API helpers for the cloud Vocabulary tab.
//
// Phase 1 surfaces GET /api/v1/vocab/tree returning the scheme/concept
// hierarchy. Phase 2 adds GET /api/v1/vocab/concept for the rich
// detail panel (alt labels, definitions, scope notes, broader,
// narrower, non-SKOS rdf:type classes). Editing endpoints
// (create/update/delete concept) land in later phases.

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";
import { useApiSession } from "./useApiSession";

export interface ConceptNode {
  uri: string;
  pref_label: string;
  children: ConceptNode[];
}

export interface SchemeNode {
  uri: string;
  pref_label: string;
  description?: string;
  top_concepts: ConceptNode[];
}

export interface VocabTreeResponse {
  schemes: SchemeNode[];
}

export function useVocabTree(graph: string | null) {
  const { token, ready } = useApiSession();
  return useQuery<VocabTreeResponse>({
    queryKey: ["vocab", "tree", graph ?? "", token ? "auth" : "noauth"],
    enabled: ready && !!graph,
    queryFn: async () => {
      if (!graph) {
        throw new Error("graph required");
      }
      const url = `/api/v1/vocab/tree?graph=${encodeURIComponent(graph)}`;
      const r = await apiFetch<VocabTreeResponse>(url, token);
      if (!r.ok) {
        throw new Error(r.message);
      }
      return r.data;
    },
  });
}

/** Walk a tree, finding the concept with the given URI (linear search; fine for thousands of concepts). */
export function findConcept(
  tree: VocabTreeResponse,
  uri: string,
): ConceptNode | null {
  for (const scheme of tree.schemes) {
    const found = findInList(scheme.top_concepts, uri);
    if (found) return found;
  }
  return null;
}

function findInList(nodes: ConceptNode[], uri: string): ConceptNode | null {
  for (const n of nodes) {
    if (n.uri === uri) return n;
    const inChild = findInList(n.children, uri);
    if (inChild) return inChild;
  }
  return null;
}

// ---- Phase 2: concept detail ----

export interface LangValue {
  value: string;
  lang?: string;
}

export interface UriLabel {
  uri: string;
  pref_label: string;
}

/** One annotation statement (native predicate vocabulary: :definition,
 *  :scopeNote, :comment, :note, :example). */
export interface AnnotationValue {
  predicate: string;
  value: string;
  lang?: string | null;
}

export interface ConceptDetail {
  uri: string;
  pref_label: string;
  alt_labels: LangValue[];
  /** Every managed annotation statement — the set the editor round-trips. */
  annotations: AnnotationValue[];
  broader: UriLabel[];
  narrower: UriLabel[];
  /** Non-SKOS rdf:type classes (e.g. vocab:Team on Affiliation/Avengers). */
  extra_types: UriLabel[];
}

export function useVocabConcept(uri: string | null, graph: string | null) {
  const { token, ready } = useApiSession();
  return useQuery<ConceptDetail>({
    queryKey: [
      "vocab",
      "concept",
      graph ?? "",
      uri ?? "",
      token ? "auth" : "noauth",
    ],
    enabled: ready && !!uri && !!graph,
    queryFn: async () => {
      if (!uri || !graph) {
        throw new Error("uri and graph required");
      }
      const url = `/api/v1/vocab/concept?uri=${encodeURIComponent(uri)}&graph=${encodeURIComponent(graph)}`;
      const r = await apiFetch<ConceptDetail>(url, token);
      if (!r.ok) {
        throw new Error(r.message);
      }
      return r.data;
    },
  });
}

// ---- Tree filter (Phase 2 search) ----

/**
 * Filter a vocab tree to only branches that contain a concept whose
 * prefLabel substring-matches `query` (case-insensitive). Ancestor
 * chains of matches are kept so the user sees context. Returns null
 * when nothing matches.
 *
 * Pure function, no React dependency — keeps the page component
 * cheap to re-render as the user types.
 */
export function filterTree(
  tree: VocabTreeResponse,
  query: string,
): VocabTreeResponse | null {
  const needle = query.trim().toLowerCase();
  if (needle === "") return tree;

  const schemes = tree.schemes
    .map((s) => {
      const tops = s.top_concepts
        .map((c) => filterConcept(c, needle))
        .filter((c): c is ConceptNode => c !== null);
      if (tops.length === 0) return null;
      return { ...s, top_concepts: tops };
    })
    .filter((s): s is SchemeNode => s !== null);

  if (schemes.length === 0) return null;
  return { schemes };
}

function filterConcept(node: ConceptNode, needle: string): ConceptNode | null {
  const selfMatches = node.pref_label.toLowerCase().includes(needle);
  const filteredChildren = node.children
    .map((c) => filterConcept(c, needle))
    .filter((c): c is ConceptNode => c !== null);
  if (selfMatches || filteredChildren.length > 0) {
    return { ...node, children: filteredChildren };
  }
  return null;
}

/**
 * Find a concept's current structural parent in the tree, so we can
 * remember it before a Move and offer Undo afterward. Returns a
 * scheme target if the concept is a top concept somewhere, a broader
 * target if it's nested under another concept, or null if the concept
 * is an orphan (rare; happens with malformed data). Undo can only
 * restore relationships the backend supports, which is scheme or
 * broader; an orphan state can't be restored exactly.
 */
export function findParentInTree(
  tree: VocabTreeResponse,
  conceptUri: string,
): { kind: "broader" | "scheme"; uri: string; label: string } | null {
  for (const scheme of tree.schemes) {
    for (const top of scheme.top_concepts) {
      if (top.uri === conceptUri) {
        return { kind: "scheme", uri: scheme.uri, label: scheme.pref_label };
      }
      const found = findBroaderInNode(top, conceptUri);
      if (found) return found;
    }
  }
  return null;
}

function findBroaderInNode(
  node: ConceptNode,
  conceptUri: string,
): { kind: "broader"; uri: string; label: string } | null {
  for (const child of node.children) {
    if (child.uri === conceptUri) {
      return { kind: "broader", uri: node.uri, label: node.pref_label };
    }
    const deeper = findBroaderInNode(child, conceptUri);
    if (deeper) return deeper;
  }
  return null;
}

/** Collect every URI in the tree (used to expand all on filter). */
export function collectAllUris(tree: VocabTreeResponse): string[] {
  const out: string[] = [];
  for (const s of tree.schemes) {
    out.push(s.uri);
    for (const c of s.top_concepts) collectFromConcept(c, out);
  }
  return out;
}

function collectFromConcept(c: ConceptNode, acc: string[]): void {
  acc.push(c.uri);
  for (const child of c.children) collectFromConcept(child, acc);
}

// ---- Phase 3: create endpoints ----

export interface CreateSchemeRequest {
  graph: string;
  pref_label: string;
  description?: string;
  /** QROS only: the qros top class the new vocabulary class subclasses (rule 5).
   * Omitted → the backend floors to :Concept (rule 8). The RDF backend ignores it. */
  base?: string;
}

export interface CreateConceptRequest {
  graph: string;
  pref_label: string;
  /** EITHER scheme (top concept) OR broader (narrower under parent). */
  scheme?: string;
  broader?: string;
  alt_labels?: string[];
  definition?: string;
  scope_note?: string;
  /** Optional additional class to dual-type the concept with (select-only). */
  additional_class?: string;
}

export interface CreateResponse {
  uri: string;
  pref_label: string;
}

export async function createScheme(
  body: CreateSchemeRequest,
  accessToken: string,
) {
  return apiFetch<CreateResponse>("/api/v1/vocab/scheme", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function createConcept(
  body: CreateConceptRequest,
  accessToken: string,
) {
  return apiFetch<CreateResponse>("/api/v1/vocab/concept", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---- Phase 4: edit + delete ----

export interface PatchConceptRequest {
  uri: string;
  graph: string;
  /**
   * `undefined` = leave unchanged. Empty string is a 400 (concepts
   * must always carry a prefLabel).
   */
  pref_label?: string;
  /**
   * `undefined` = leave unchanged. A list REPLACES every managed annotation
   * statement (definition/scopeNote/comment/note/example) with exactly the
   * list; `[]` clears them all. Alt labels have their own field.
   */
  annotations?: AnnotationValue[];
  /**
   * Move the concept under this concept (replaces existing
   * skos:broader / skos:topConceptOf). Mutually exclusive with
   * `scheme`.
   */
  broader?: string;
  /**
   * Promote the concept to a top concept of this scheme (replaces
   * existing skos:broader / skos:topConceptOf). Mutually exclusive
   * with `broader`.
   */
  scheme?: string;
  /**
   * `undefined` = leave unchanged. A list REPLACES the whole set of the
   * concept's classes (`[]` clears all). Each entry must be a known class
   * (validated server-side). Lets the UI show every class and remove the
   * ones a bad import added.
   */
  classes?: string[];
  /**
   * `undefined` = leave unchanged. A list REPLACES the whole
   * alternative-label set (desktop semantics: replace-not-append);
   * `[]` clears.
   */
  alt_labels?: string[];
}

export interface PatchConceptResponse {
  uri: string;
  pref_label?: string;
  /** Echo of the applied annotation set when one was supplied. */
  annotations?: AnnotationValue[];
  /** Echo of the new parent when a Move happened. */
  moved_to?: {
    kind: "broader" | "scheme";
    uri: string;
  };
}

export async function patchConcept(
  body: PatchConceptRequest,
  accessToken: string,
) {
  return apiFetch<PatchConceptResponse>("/api/v1/vocab/concept", accessToken, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface DeleteConceptParams {
  uri: string;
  graph: string;
}

/**
 * Server returns 409 with `narrower_count` and a human-readable
 * `detail` when the concept has narrower terms. The SPA surfaces
 * that detail directly to the taxonomist so they can clear children
 * first.
 */
export async function deleteConcept(
  params: DeleteConceptParams,
  accessToken: string,
) {
  const url = `/api/v1/vocab/concept?uri=${encodeURIComponent(params.uri)}&graph=${encodeURIComponent(params.graph)}`;
  // Server responds with 204 No Content on success; apiFetch returns
  // { ok: true, data: null } for that, so a `null` type parameter is
  // truthful (rather than the previous { ok: true } phantom which
  // tripped on JSON parse of an empty body).
  return apiFetch<null>(url, accessToken, { method: "DELETE" });
}

export interface PatchSchemeRequest {
  uri: string;
  graph: string;
  /** `undefined` = leave unchanged. Empty string is a 400 (a scheme must stay identifiable). */
  title?: string;
  /** `undefined` = leave unchanged. Empty string clears the description. */
  description?: string;
}

export async function patchScheme(
  body: PatchSchemeRequest,
  accessToken: string,
) {
  return apiFetch<null>("/api/v1/vocab/scheme", accessToken, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/**
 * Cascade delete — the scheme AND every concept in it (the desktop
 * "Delete Scheme & All Concepts" contract; the caller two-step confirms).
 * With `reassignTo`, member terms are re-homed onto that class and only the
 * class is deleted (nothing removed). Server returns removed + reassigned counts.
 */
export async function deleteScheme(
  params: DeleteConceptParams & { reassignTo?: string },
  accessToken: string,
) {
  let url = `/api/v1/vocab/scheme?uri=${encodeURIComponent(params.uri)}&graph=${encodeURIComponent(params.graph)}`;
  if (params.reassignTo) {
    url += `&reassign_to=${encodeURIComponent(params.reassignTo)}`;
  }
  return apiFetch<{ members_removed: number; members_reassigned: number }>(
    url,
    accessToken,
    { method: "DELETE" },
  );
}
